import type { Pack } from '../engine/schema.js';
import { readRoute, readUrl, writeUrl, type Route } from './url.js';

export type Mode = 'plain' | 'analyst';

export interface AppState {
  /** Which jurisdiction is being shown. */
  packSlug: string;
  scenarioId: string;
  mode: Mode;
  /** Input id → user-set value. Only adjustable inputs may appear. */
  overrides: Record<string, number>;
  /**
   * Outcome ids the reader said matter to them, in the order they picked.
   * These reorder the page and populate the summary. They never filter it, and
   * they never combine into a score.
   */
  outcomes: string[];
  /** Metric or input id whose provenance drawer is open. */
  openDrawer: string | null;
  /** Data version the current URL was made against, if it differs from ours. */
  linkedDataVersion: number | null;
}

type Listener = (s: AppState) => void;

export interface StoreOptions {
  knownSlugs: readonly string[];
  defaultSlug: string;
  resolve: (slug: string) => Pack;
}

export function createStore(opts: StoreOptions) {
  let route: Route = readRoute(opts.knownSlugs, opts.defaultSlug);
  let pack: Pack = opts.resolve(route.slug);
  let state: AppState = readUrl(pack, route);
  const listeners = new Set<Listener>();

  const notify = () => {
    for (const l of listeners) l(state);
  };

  const store = {
    get(): AppState {
      return state;
    },

    /** The pack the current state refers to. Never stale against `packSlug`. */
    pack(): Pack {
      return pack;
    },

    route(): Route {
      return route;
    },

    subscribe(l: Listener): () => void {
      listeners.add(l);
      return () => listeners.delete(l);
    },

    /**
     * `history: 'push'` for discrete acts — a scenario change, a mode switch,
     * opening a drawer. `'replace'` for slider drags, so back undoes the
     * decision rather than each pixel of movement.
     */
    set(patch: Partial<AppState>, history: 'push' | 'replace' = 'push'): void {
      const next = { ...state, ...patch };

      // Switching jurisdiction invalidates everything keyed to the old pack:
      // scenario ids and override input ids are pack-local, and carrying them
      // across would either throw or, worse, silently match an unrelated id.
      if (next.packSlug !== state.packSlug) {
        pack = opts.resolve(next.packSlug);
        route = { ...route, slug: next.packSlug };
        next.scenarioId = pack.meta.defaultScenario;
        next.overrides = {};
        // Outcomes name metrics, and metric ids are pack-local.
        next.outcomes = [];
        next.openDrawer = null;
        next.linkedDataVersion = null;
      }

      state = next;
      writeUrl(pack, state, route, history);
      notify();
    },

    setOverride(id: string, value: number): void {
      store.set(
        { overrides: { ...state.overrides, [id]: value } },
        // Dragging is continuous; do not flood the history stack.
        'replace',
      );
    },

    clearOverride(id: string): void {
      const next = { ...state.overrides };
      delete next[id];
      store.set({ overrides: next });
    },

    resetOverrides(): void {
      store.set({ overrides: {} });
    },

    /** Add or remove an outcome, preserving pick order for the summary. */
    toggleOutcome(id: string): void {
      const current = state.outcomes;
      store.set({
        outcomes: current.includes(id)
          ? current.filter((o) => o !== id)
          : [...current, id],
      });
    },

    clearOutcomes(): void {
      store.set({ outcomes: [] });
    },

    /**
     * Re-read from the URL after a back/forward navigation. The route is
     * re-read too — going back may cross a project boundary.
     */
    rehydrate(): void {
      route = readRoute(opts.knownSlugs, opts.defaultSlug);
      pack = opts.resolve(route.slug);
      state = readUrl(pack, route);
      notify();
    },
  };

  window.addEventListener('popstate', () => store.rehydrate());

  return store;
}

export type Store = ReturnType<typeof createStore>;
